import { createContext, useContext, useEffect } from 'react';

/**
 * Wizard-level navigation actions published by {@link SubscriptionBlockWizardLayout}
 * so step footers can render the back button without prop-drilling.
 */
export type WizardNavigationContextValue = {
  onBack?: () => void;
  /** Locking hides the layout's close and back buttons, so a step in flight cannot be left. */
  setLocked: (locked: boolean) => void;
};

const WizardNavigationContext = createContext<WizardNavigationContextValue | null>(null);

/** Stands in for the context outside the wizard, where there is no back action and nothing to lock. */
const UNPROVIDED_NAVIGATION: WizardNavigationContextValue = { setLocked: () => undefined };

/**
 * Provider for the wizard navigation context. The wizard layout mounts it with the
 * back handler resolved by the orchestrator (`BlockCreationWizard`); steps consume it
 * through {@link useWizardNavigation}.
 */
export const WizardNavigationProvider = WizardNavigationContext.Provider;

/**
 * Reads the wizard navigation actions from context.
 *
 * Tolerates rendering outside the wizard, where the back button has to disappear rather than crash.
 *
 * @returns The context value — `onBack` is undefined on steps without back navigation
 */
export const useWizardNavigation = (): WizardNavigationContextValue =>
  useContext(WizardNavigationContext) ?? UNPROVIDED_NAVIGATION;

/** A lock taken outside the wizard would hold nothing, so it fails loudly instead. */
const useProvidedWizardNavigation = (): WizardNavigationContextValue => {
  const context = useContext(WizardNavigationContext);
  if (!context) throw new Error('useWizardLock must be used within WizardNavigationProvider');

  return context;
};

/** Holds the wizard locked while `locked` is true, and releases it when the step unmounts. */
export const useWizardLock = (locked: boolean): void => {
  const { setLocked } = useProvidedWizardNavigation();

  useEffect(() => {
    setLocked(locked);
    return () => setLocked(false);
  }, [locked, setLocked]);
};
